import React, { useEffect, useState } from 'react'
import {
  Alert, Button, Input, Modal, message,
} from 'antd'
import { CopyOutlined } from '@ant-design/icons'

import type { LinkedAccountItem } from '../../types/linkedAccount'
import { revealLinkedAccountPassword } from '../../apis/linkedAccountApis'
import {
  isForbiddenError, isNotFoundError, isWrongLoginPasswordError,
} from '../../util/linkedAccount/linkedAccountErrors'
import { useIsMobile } from '../../hook/useMediaQuery'
import {
  LINKED_ACCOUNT_DIALOG_CLASS,
  LINKED_ACCOUNT_DIALOG_PROMPT_CLASS,
  LINKED_ACCOUNT_PROMPT_MODAL_WIDTH_PX,
  LINKED_ACCOUNT_SHORT_MODAL_WIDTH_PX,
} from '../../constants/linkedAccount'
import PasswordPromptDialog from './PasswordPromptDialog'

/**
 * "Nhận mật khẩu" (SP-005, Task 4) — hai trạng thái trong CÙNG một lần mở:
 * 1. chưa xác thực: `PasswordPromptDialog` hỏi mật khẩu ERP của chính người đang xem;
 * 2. đã xác thực: hộp thoại thứ hai hiện mật khẩu tài khoản liên kết vừa giải mã, một lần.
 *
 * 🔴 BẢO MẬT — lý do tồn tại của cả file này (đọc kỹ trước khi sửa):
 * - Mật khẩu giải mã CHỈ sống trong state cục bộ `password` của component này. KHÔNG BAO GIỜ ghi
 *   vào store/cache/`localStorage`/`sessionStorage`/biến cấp module/`console`.
 * - Component KHÔNG unmount khi đóng (Task 6 luôn render nó, chỉ đổi prop `item`, đúng khuôn các
 *   drawer chi tiết đơn đã ghi trong CLAUDE.md) ⇒ mọi đường đóng (nút Đóng, icon `×`, hoặc parent tự
 *   set `item=null`) đều phải xoá `password` NGAY — không đợi hộp thoại unmount rồi mới "quên".
 *   Effect khoá theo `item?.id` (chạy cả lúc đóng lẫn lúc mở lại) CỘNG với xoá trực tiếp trong
 *   `handleClose` (không phụ thuộc việc nơi gọi có kịp đổi prop hay không) — hai lớp, không phải
 *   thừa: lớp thứ hai là NGAY LẬP TỨC trong cùng lượt render, lớp thứ nhất là lưới đỡ nếu component
 *   bị mở lại với item khác mà không đi qua trạng thái đóng.
 * - Không có "phiên xem mật khẩu": đóng rồi mở lại LUÔN quay về trạng thái 1, phải xác thực lại.
 *
 * DOM hook cho Task 6 / bộ test trình duyệt:
 * - Trạng thái 1: tái dùng nguyên DOM hook của `PasswordPromptDialog`.
 * - `.reveal-password-modal` — className của `Modal` trạng thái 2.
 * - `.reveal-password-modal__alert` — banner "Xác thực thành công…".
 * - `.reveal-password-modal__value-input` — ô chỉ đọc chứa mật khẩu (`input[readonly]`).
 * - `.reveal-password-modal__copy-btn` — nút Copy, ICON-ONLY (Figma `26:10062`, fix round 1) —
 *   không có chữ nhìn thấy được, tên truy cập đặt qua `aria-label="Sao chép mật khẩu"`.
 * - `.reveal-password-modal__close-btn` — nút Đóng (nút duy nhất ở footer).
 *
 * 🔴 Responsive ≤767px (SP-005, Task 9): hộp thoại NGẮN (BRD §10) — trạng thái 1 tự lo qua
 * `PasswordPromptDialog` (đã tự đổi `width`); trạng thái 2 (`Modal` ở đây) đổi TRỰC TIẾP từ `480`
 * sang `LINKED_ACCOUNT_SHORT_MODAL_WIDTH_PX` (343) qua `useIsMobile()`. KHÔNG thành overlay toàn màn.
 */

export interface RevealPasswordModalProps {
  /** `null` = đóng. Khác `null` = mở cho bản ghi này. */
  item: LinkedAccountItem | null
  onClose: () => void
  /** Gọi khi BE trả 404 — nơi gọi phải làm mới danh sách. */
  onRecordGone: () => void
}

const MODAL_TITLE = 'Nhận mật khẩu'
const PROMPT_DESCRIPTION = 'Nhập mật khẩu tài khoản của bạn để xem mật khẩu tài khoản liên kết.'
const WRONG_PASSWORD_MESSAGE = 'Mật khẩu không đúng. Vui lòng thử lại.'
const FORBIDDEN_MESSAGE = 'Bạn không có quyền xem mật khẩu của tài khoản này.'
const GENERIC_ERROR_MESSAGE = 'Không lấy được mật khẩu. Vui lòng thử lại.'
const SUCCESS_MESSAGE = 'Xác thực thành công. Dưới đây là mật khẩu của bạn.'
const REVEALED_LABEL = 'Mật khẩu tài khoản liên kết:'
const CLOSE_LABEL = 'Đóng'
/**
 * Nút Copy là ICON-ONLY (Figma node `26:10062`, fix round 1) — không có nhãn chữ nhìn thấy được,
 * "Copy" tiếng Anh trước đó là suy diễn từ một tiền lệ không liên quan và lạc lõng giữa một hộp
 * thoại toàn tiếng Việt. Icon `CopyOutlined` đúng ý — Figma vẽ ba chấm nhưng đó là placeholder
 * chung của design system cho MỌI nút icon (cùng instance với nút `…`), không phải chỉ định glyph.
 */
const COPY_ARIA_LABEL = 'Sao chép mật khẩu'
const COPY_SUCCESS_MESSAGE = 'Đã sao chép mật khẩu'
const COPY_ERROR_MESSAGE = 'Không sao chép được. Vui lòng chọn và sao chép thủ công.'

const RevealPasswordModal = ({ item, onClose, onRecordGone }: RevealPasswordModalProps): JSX.Element => {
  const [password, setPassword] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const isMobile = useIsMobile()

  // Đổi bản ghi (gồm cả đóng, item?.id === undefined) ⇒ quên sạch — không có "phiên xem mật khẩu".
  useEffect(() => {
    setPassword(null)
    setSubmitting(false)
    setErrorMessage(null)
  }, [item?.id])

  /** Đóng NGAY LẬP TỨC ở tầng state cục bộ, không đợi nơi gọi đổi prop `item`. */
  const handleClose = (): void => {
    setPassword(null)
    setSubmitting(false)
    setErrorMessage(null)
    onClose()
  }

  const handleSubmit = async (currentPassword: string): Promise<void> => {
    if (item === null) return
    setSubmitting(true)
    setErrorMessage(null)
    try {
      const revealed = await revealLinkedAccountPassword(item.id, currentPassword)
      setPassword(revealed)
    } catch (error) {
      if (isWrongLoginPasswordError(error)) {
        setErrorMessage(WRONG_PASSWORD_MESSAGE)
      } else if (isNotFoundError(error)) {
        handleClose()
        onRecordGone()
      } else if (isForbiddenError(error)) {
        setErrorMessage(FORBIDDEN_MESSAGE)
      } else {
        setErrorMessage(GENERIC_ERROR_MESSAGE)
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleCopy = async (): Promise<void> => {
    if (password === null) return
    try {
      // `navigator.clipboard` có thể không tồn tại (http) — truy cập `.writeText` trên `undefined`
      // ném lỗi ĐỒNG BỘ, `await` một promise bị reject ném lỗi BẤT ĐỒNG BỘ — cả hai đều rơi vào
      // đúng khối `catch` này vì nằm trong hàm `async`.
      await navigator.clipboard.writeText(password)
      message.success(COPY_SUCCESS_MESSAGE)
    } catch {
      message.error(COPY_ERROR_MESSAGE)
    }
  }

  const promptOpen = item !== null && password === null

  return (
    <>
      <PasswordPromptDialog
        open={promptOpen}
        title={MODAL_TITLE}
        description={PROMPT_DESCRIPTION}
        submitting={submitting}
        errorMessage={errorMessage}
        onCancel={handleClose}
        onSubmit={handleSubmit}
      />
      <Modal
        className={`${LINKED_ACCOUNT_DIALOG_CLASS} ${LINKED_ACCOUNT_DIALOG_PROMPT_CLASS} reveal-password-modal`}
        open={item !== null && password !== null}
        width={isMobile ? LINKED_ACCOUNT_SHORT_MODAL_WIDTH_PX : LINKED_ACCOUNT_PROMPT_MODAL_WIDTH_PX}
        destroyOnClose
        maskClosable={false}
        title={MODAL_TITLE}
        onCancel={handleClose}
        footer={[
          <Button key="close" type="primary" size="large" className="reveal-password-modal__close-btn" onClick={handleClose}>
            {CLOSE_LABEL}
          </Button>,
        ]}
      >
        <Alert
          className="reveal-password-modal__alert"
          type="success"
          showIcon
          message={SUCCESS_MESSAGE}
        />
        <div className="reveal-password-modal__field">
          <div className="reveal-password-modal__label">{REVEALED_LABEL}</div>
          <div className="reveal-password-modal__value">
            <Input
              readOnly
              className="reveal-password-modal__value-input"
              value={password ?? ''}
            />
            <Button
              type="text"
              className="reveal-password-modal__copy-btn"
              aria-label={COPY_ARIA_LABEL}
              icon={<CopyOutlined />}
              onClick={handleCopy}
            />
          </div>
        </div>
      </Modal>
    </>
  )
}

export default RevealPasswordModal
